import React, { memo } from 'react';

import PropTypes from 'prop-types';

import { FulfillmentPlannerState } from './fulfillment-planner-form-state';
import messages from './messages';
import { OrderDetailsCanvasHotkeys } from '../../../../components/hotkeys/advanced-hotkeys/order-details-canvas-hotkeys';
import OrderAwaitingPayment from '../../../../components/orders-drawer/sections/order-awaiting-payment';
import { RestoreCancelled } from '../../../../components/restore/restore-cancelled-button';
import { RestoreShipped } from '../../../../components/restore/restore-shipped-button';
import { useFulfillmentPlanIdContext } from '../../../../fulfillment-plans/context/fulfillment-plan-id-context-provider';
import { useSalesOrderIdContext } from '../../../context/sales-order-id-context-provider';
import { OrderDetailsTabSection } from '../../tab-section/section';
import { Cancelled, ConfigureShipment, ImportedAsShipped, OtherShippingOptions, RateCardReadOnlyFulfillments } from '../sections';
import { useEnableReship } from '../../../../feature-flags/hooks/use-enable-reship';

export const FulfillmentPlannerSections = memo(({ userHasCorrectRoles, fulfillmentPlannerState, isImportedAsShipped }) => {
  const salesOrderId = useSalesOrderIdContext();
  const fulfillmentPlanId = useFulfillmentPlanIdContext();
  const isReshipEnabled = useEnableReship();

  if (userHasCorrectRoles && fulfillmentPlannerState === FulfillmentPlannerState.Editable) {
    return (
      <>
        <RateCardReadOnlyFulfillments fulfillmentPlanId={fulfillmentPlanId} />
        <ConfigureShipment />
        <OtherShippingOptions />
        <OrderDetailsCanvasHotkeys fulfillmentPlanId={fulfillmentPlanId} />
      </>
    );
  }

  if (isImportedAsShipped) {
    return (
      <>
        <RateCardReadOnlyFulfillments fulfillmentPlanId={fulfillmentPlanId} />
        {isReshipEnabled ? (
          <ImportedAsShipped />
        ) : (
          <OrderDetailsTabSection ariaLabelMessageDescriptor={messages.restoreShipmentAriaLabel}>
            <RestoreShipped fulfillmentPlanId={fulfillmentPlanId} />
          </OrderDetailsTabSection>
        )}
      </>
    );
  }

  if (fulfillmentPlannerState === FulfillmentPlannerState.Readonly) {
    return (
      <>
        <RateCardReadOnlyFulfillments fulfillmentPlanId={fulfillmentPlanId} showFulfillmentDisplay />
        <OtherShippingOptions isReadOnly />
      </>
    );
  }

  if (fulfillmentPlannerState === FulfillmentPlannerState.AwaitingPayment) {
    return (
      <>
        <RateCardReadOnlyFulfillments fulfillmentPlanId={fulfillmentPlanId} />
        <OrderDetailsTabSection ariaLabelMessageDescriptor={messages.awaitingPaymentSectionAriaLabel}>
          <OrderAwaitingPayment salesOrderId={salesOrderId} />
        </OrderDetailsTabSection>
      </>
    );
  }

  if (fulfillmentPlannerState === FulfillmentPlannerState.Cancelled) {
    return (
      <>
        <RateCardReadOnlyFulfillments fulfillmentPlanId={fulfillmentPlanId} />
        {isReshipEnabled ? (
          <Cancelled />
        ) : (
          <OrderDetailsTabSection ariaLabelMessageDescriptor={messages.cancelledSectionAriaLabel}>
            <RestoreCancelled fulfillmentPlanId={fulfillmentPlanId} />
          </OrderDetailsTabSection>
        )}
      </>
    );
  }

  return null;
});

FulfillmentPlannerSections.propTypes = {
  userHasCorrectRoles: PropTypes.bool.isRequired,
  fulfillmentPlannerState: PropTypes.oneOf(Object.values(FulfillmentPlannerState)).isRequired,
  isImportedAsShipped: PropTypes.bool.isRequired,
};
